import { FC, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Navigate, useNavigate } from 'react-router-dom';
import { Loading } from 'framework/ui/mui-builder';
import { FormShowTemplate } from 'framework/form';
import { Breadcrumbs, ListBreadcrumbItem } from 'framework/ui/BreadCrumbs';
import { EntityHeader, ShowPageLayout } from 'framework/ui/layout';
import { useProjectDataContext, useNewProjectUrl } from 'domain/shared/newProject';
import { ReuseGroup } from './types';
import { useReuseData } from './useReuseData';
import { TransferFormValues } from './useReuseDataService';
import { reuseDataMilestonesValidation } from './reuseDataValidation';
import {
  PredecessorOutcomes,
  PredecessorMilestones,
  PredecessorRisks,
  PredecessorStakeholders,
  PredecessorAssumptions,
  FormButtons,
} from './components';

const GROUP_SECTIONS: Record<ReuseGroup, FC> = {
  businessOutcomes: PredecessorOutcomes,
  milestones: PredecessorMilestones,
  risks: PredecessorRisks,
  stakeholders: PredecessorStakeholders,
  assumptions: PredecessorAssumptions,
};

const ReuseDataPage: FC = () => {
  const { data: project } = useProjectDataContext();
  const { getOutcomesUrl } = useNewProjectUrl();
  const navigate = useNavigate();
  const { predecessor, groupData, groupsToTransfer, groupsToShow, loading, transferData, isSubmitting } =
    useReuseData();

  const [lockedGroups, setLockedGroups] = useState<ReuseGroup[]>([]);
  const [formKey, setFormKey] = useState(0);

  if (loading) {
    return <Loading isInitialPosition />;
  }

  if (!predecessor || groupsToShow.length === 0) {
    return <Navigate to={getOutcomesUrl(project.id)} replace />;
  }

  const groupsToSubmit = groupsToTransfer.filter(group => !lockedGroups.includes(group));

  const handleSubmit = async (formValues: object) => {
    const succeeded = await transferData(formValues as TransferFormValues, groupsToSubmit);
    if (succeeded.length) {
      setLockedGroups(prev => [...prev, ...succeeded]);
    }
    setFormKey(key => key + 1);
    return formValues;
  };

  const hasGroupsToTransfer = groupsToSubmit.length > 0;

  const initialData = groupsToShow.reduce<Record<string, unknown>>(
    (acc, group) => ({ ...acc, [group]: groupData[group] }),
    {},
  );

  return (
    <ShowPageLayout
      breadcrumbs={
        <Breadcrumbs>
          <ListBreadcrumbItem entity={project.name ?? 'Project'} link={getOutcomesUrl(project.id)} />
          <ListBreadcrumbItem entity="Reuse data" />
        </Breadcrumbs>
      }
      entityHeader={<EntityHeader entityTitle="Reuse data from previous project" />}
    >
      <FormShowTemplate
        key={formKey}
        initEditMode
        isInitialsLoadingPosition
        initialData={initialData}
        submitFormFunc={handleSubmit}
        getValidationSchemaFunc={() => Promise.resolve(reuseDataMilestonesValidation)}
      >
        <Stack spacing={4}>
          {isSubmitting && <Loading hasBackground />}
          {groupsToShow.map(group => {
            const Section = GROUP_SECTIONS[group];
            const isLocked = lockedGroups.includes(group);
            return (
              <Box key={group} sx={isLocked ? { pointerEvents: 'none', opacity: 0.5 } : undefined}>
                <Section />
              </Box>
            );
          })}
          {hasGroupsToTransfer && <FormButtons onCancel={() => navigate(getOutcomesUrl(project.id))} />}
        </Stack>
      </FormShowTemplate>
    </ShowPageLayout>
  );
};

export default ReuseDataPage;
